<script setup lang="ts">
import { ref } from 'vue';

import { AppImg } from '@libs/ui';

import { useCoreStore } from '../../../../../../core/stores';
import type {
  AccommodationProductCardData,
  AccommodationSegmentContext,
} from '../../../../models';
import AccommodationProductCardContent from '../AccommodationProductCardContent.vue';
import AccommodationCardSelectedFooter from '../AccommodationCardSelectedFooter.vue';
import DateCardTag from '../../DateCardTag.vue';

import MobileProductCardLayout from './MobileProductCardLayout.vue';

interface AccommodationProductCardMobileProps {
  productCardData: AccommodationProductCardData;
  context: AccommodationSegmentContext;
  isMetaExperiment: boolean;
}

const props = defineProps<AccommodationProductCardMobileProps>();

interface AccommodationProductCardMobileEmits {
  (event: 'show-details'): void;
  (event: 'show-rooms'): void;
}

const emit = defineEmits<AccommodationProductCardMobileEmits>();

const coreStore = useCoreStore();

const enableHoverColor = ref<boolean>(false);
</script>

<template>
  <MobileProductCardLayout
    class="accommodation-product-card-mobile"
    @click.stop="emit('show-details')"
  >
    <template #top-section>
      <AppImg
        class="image"
        :src="productCardData.imgSrcs[0] || ''"
        :alt="$t('a11y.hotel_image')"
        @img-load-error="
          (src) =>
            coreStore.handleImageLoadError({
              src: src,
              source: 'accommodation-product-card-mobile-image',
            })
        "
      />
    </template>
    <template #content>
      <AccommodationProductCardContent
        :product-card-content="props.productCardData.productCardContent"
        :context="props.context"
        :print-mode="false"
        @mouse-enter-content="enableHoverColor = true"
        @mouse-leave-content="enableHoverColor = false"
        @show-details="emit('show-details')"
        @show-rooms="emit('show-rooms')"
      />
    </template>
    <template #footer>
      <AccommodationCardSelectedFooter
        :ingredient-index="props.context.ingredientIndex"
        :is-meta-experiment="props.isMetaExperiment"
      />
    </template>
    <template #top-section-tag>
      <DateCardTag :date="props.productCardData.date" />
    </template>
  </MobileProductCardLayout>
</template>

<style lang="scss" scoped>
.accommodation-product-card-mobile {
  position: relative;

  .image {
    aspect-ratio: 4 / 3;
  }

  .footer {
    display: flex;
    justify-content: space-between;
    border-top: 1px solid var(--color-neutral-30);
    padding: 20px var(--spacing-12);

    .label {
      align-self: center;
      @include font-label-m-bold;
      line-height: var(--line-heights-14);
      padding: var(--spacing-8) var(--spacing-12);
      border-radius: var(--spacing-8);
      background-color: var(--color-brand-lilac);
    }
  }
}
</style>
